import type { Item, Metadata, SerializeData } from './store';
import pdfMake, { type Content } from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TableCell } from 'pdfmake/interfaces';

pdfMake.addVirtualFileSystem(pdfFonts);

function blank(n: number) {
  return Array(n).fill([]);
}

export function printData(data: SerializeData) {
  const ext = !data.image.startsWith('data:');
  const generator = pdfMake.createPdf({
    info: {
      title: `${data.metadata.surname} - Issue Form`,
    },
    images: {
      logo: window.location.origin + data.image,
    },
    content: [
      data.image
        ? {
            image: ext ? 'logo' : data.image,
            height: data.imageHeight,
          }
        : [],
      printItems(data.items, data.metadata),
    ],
    styles: {
      header: {
        fontSize: 12,
        bold: true,
        alignment: 'center',
        marginTop: 10,
      },
      metaHeader: {
        fontSize: 6,
        bold: true,
        alignment: 'left',
        marginBottom: 2,
      },
      metaContent: {
        fontSize: 10,
        alignment: 'left',
        color: '#333',
      },
      itemHeader: {
        fontSize: 8,
        bold: true,
        alignment: 'center',
      },
      itemContent: {
        fontSize: 8,
        alignment: 'left',
        color: '#333',
        margin: [0, 2, 0, 2],
      },
      itemSerial: {
        fontSize: 8,
        alignment: 'left',
        color: '#1447e6',
        italics: true,
      },
      footHeader: {
        fontSize: 8,
        bold: true,
        alignment: 'center',
      },
      footDate: {
        fontSize: 6,
        bold: true,
        alignment: 'center',
        color: '#555',
        margin: [2, 2, 2, 2],
      },
      footNumber: {
        fontSize: 8,
        bold: true,
        alignment: 'center',
        margin: [0, 2, 0, 2],
      },
    },
    header: function (currentPage, pageCount) {
      return {
        margin: [40, 20, 40, 0], // Left, top, right, bottom
        stack: [
          data.showPageNumbers
            ? {
                text: `Page ${currentPage} / ${pageCount}`,
                alignment: 'right',
                marginTop: 4,
                marginRight: 4,
              }
            : [],
        ],
      };
    },
    footer: function (currentPage, pageCount) {
      return {
        margin: [40, 0, 40, 0], // Left, top, right, bottom
        stack: [
          {
            stack: [
              'This form shall be verified monthly to ensure that the requirement still exists and re-signed annually when the temporary issued period exceeds one year.',
              "Ce formulaire doit être vérifié mensuellement afin de s'assurer que le besoin existe toujours et re-signé annuellement si la période de prêt dépasse un an.",
            ],
            fontSize: 7,
            bold: true,
            alignment: 'center',
            margin: [0, 0, 0, 4],
          },
          {
            text: 'ACKNOWLEGEMENT BY INDIVIDUAL OF RECEIPT OF STORES LISTED IN: – ACCUSÉ DE RÉCEPTION DES MARCHANDISES INDIQUÉES À LA :',
            fontSize: 8,
            alignment: 'center',
          },
          {
            table: {
              widths: ['auto', '*', '15%', 'auto', '*', '15%'],
              body: [
                [
                  ...blank(2).flatMap<Content>(() => [
                    {
                      text: 'COL',
                      style: 'footHeader',
                      verticalAlignment: 'middle',
                    },
                    {
                      text: 'SIGNATURE',
                      style: 'footHeader',
                      verticalAlignment: 'middle',
                    },
                    {
                      table: {
                        body: [
                          [
                            {
                              text: 'DATE',
                              style: 'footHeader',
                              verticalAlignment: 'middle',
                            },
                            {
                              text: '(YYYY-MM-DD)',
                              style: 'footDate',
                              verticalAlignment: 'middle',
                            },
                          ],
                        ],
                      },
                      style: 'footHeader',
                      layout: {
                        defaultBorder: false,
                        paddingLeft: () => 0,
                        paddingRight: () => 0,
                        paddingTop: () => 0,
                        paddingBottom: () => 0,
                      },
                    },
                  ]),
                ],
                ...blank(5).map((_, index) => [
                  { text: index + 1, style: 'footNumber' },
                  { text: '', style: 'footNumber' },
                  { text: '', style: 'footNumber' },
                  { text: index + 6, style: 'footNumber' },
                  { text: '', style: 'footNumber' },
                  { text: '', style: 'footNumber' },
                ]),
              ],
            },
          },
          data.showPageNumbers
            ? {
                text: `Page ${currentPage} / ${pageCount}`,
                alignment: 'right',
                marginTop: 4,
                marginRight: 4,
              }
            : [],
        ],
      };
    },
    pageMargins: [40, 40, 40, 180],
  });
  return generator;
}

function printMetadata(meta: Metadata): TableCell[][] {
  function el(name: string, text: string, colSpan = 1) {
    return {
      stack: [
        {
          text: name.toUpperCase(),
          style: 'metaHeader',
        },
        {
          text,
          style: 'metaContent',
        },
      ],
      colSpan,
    };
  }
  return [
    [
      {
        text: 'TEMPORARY ISSUE TO AN INDIVIDUAL — PRÊT DE MATÉRIEL À UN PARTICULIER',
        style: 'header',
        border: [false, true, false, false],
        borderColor: ['#fff', '#fff', '#fff', '#fff'],
        colSpan: 12,
      },
      ...blank(11),
    ],
    [
      {
        text: meta.extra,
        border: [false, false, false, false],
        style: 'metaContent',
        verticalAlignment: 'middle',
      },
      el('Surname — Nom de famille', meta.surname),
      el('Rank — Grade', meta.rank, 3),
      ...blank(2),
      el('PRI / SN — CIDP / NM', meta.serial, 7),
      ...blank(6),
    ],
    [
      el('Unit — Unité', meta.unit),
      el('Location — Endroit', meta.location),
      el('Telephone', meta.telephone, 10),
      ...blank(9),
    ],
  ];
}

function printItems(items: Item[], meta: Metadata): Content {
  if (items.length === 0) return [];
  return {
    table: {
      widths: ['auto', '*', ...Array(10).fill(10)],
      body: [
        ...printMetadata(meta),
        [
          {
            stack: ['STOCK NUMBER', 'NUMÉRO DE NOMENCLATURE'],
            rowSpan: 2,
            style: 'itemHeader',
            verticalAlignment: 'middle',
          },
          {
            text: 'DESCRIPTION — DÉSIGNATION',
            rowSpan: 2,
            style: 'itemHeader',
            verticalAlignment: 'middle',
          },
          {
            text: 'QUANTITY — QUANTITÉ',
            colSpan: 10,
            style: 'itemHeader',
            verticalAlignment: 'middle',
          },
          ...blank(9),
        ],
        [
          [],
          [],
          ...blank(10).map((_, i) => ({
            text: (i + 1).toString(),
            style: 'itemHeader',
          })),
        ],
        ...items.map(item => printItem(item)),
      ],
      headerRows: 5,
    },
  };
}

function printItem(item: Item): TableCell[] {
  const { stockNumber, description, serials, quantities } = item;
  return [
    {
      text: stockNumber,
      style: 'itemContent',
    },
    {
      text: [
        description,
        ...(serials ?? []).map(serial => ({
          text: ` - ${serial}`,
          style: 'itemSerial',
        })),
      ],
      style: 'itemContent',
    },
    ...quantities.map(
      q =>
        ({
          text: q?.toString() || '',
          style: 'itemContent',
          alignment: 'center',
        }) as const,
    ),
  ];
}
